import * as Sentry from '@sentry/react';
import { createRoot } from 'react-dom/client';
import {
  createTheme,
  MantineProvider,
  MantineThemeOverride,
} from '@mantine/core';
import { lazy, Suspense } from 'react';
const App = lazy(() => import('./App.tsx'));
const TrustPilotCarousel = lazy(() => import('./components/TrustPilotCarousel.tsx'));
import '@mantine/core/styles.css';
import { useEffect } from 'react';

import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import './index.css';
import { configureStore } from '@reduxjs/toolkit';
import FormRegistrationStore from './features/FormRegistration.ts';
import ViewportStore from './features/Viewport.ts';
import collapseStore from './features/collapseSlice.ts';
import ahmStore from './features/ahm.ts';
import compareStore from './features/Compare.ts';
import '@mantine/dates/styles.css';
import TagManager from 'react-gtm-module';
import '@mantine/core/styles.css';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';


// Preload the Step0 component to ensure it's loaded first
import('./steps/Step0.tsx').catch(console.error);

const sentryReduxEnhancer = Sentry.createReduxEnhancer({
  // Optionally pass options listed below
});

const store = configureStore({
  reducer: {
    formRegistration: FormRegistrationStore,
    viewport: ViewportStore,
    collapse: collapseStore,
    ahm: ahmStore,
    compare: compareStore,
  },
  enhancers: (getDefaultEnhancers) => {
    return getDefaultEnhancers().concat(sentryReduxEnhancer);
  },
});

const customColors: MantineThemeOverride['colors'] = {
  myBlue: [
    '#17385D', // Lightest shade
    '#17385D',
    '#17385D',
    '#17385D',
    '#17385D', // Base shade
    '#17385D',
    '#17385D',
    '#17385D',
    '#17385D', // Darkest shade
    '#17385D',
  ] as const, // Ensure it's treated as a tuple
};
const theme = createTheme({
  colors: customColors,
  primaryColor: 'myBlue',
  components: {
    TextInput: {
      defaultProps: {
        styles: {
          input: {
            height: '48px',
          },
          wrapper: {
            height: '48px',
          },
        },
      },
    },
    NumberInput: {
      defaultProps: {
        styles: {
          input: {
            height: '48px',
          },
          wrapper: {
            height: '48px',
          },
        },
      },
    },
  },
});

// Loading indicator component - Making it more visible for PageSpeed
const LoadingIndicator = () => (
  <div className="fixed top-0 left-0 h-screen w-screen bg-white z-50 flex flex-col items-center justify-center">
    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mb-4"></div>
    <h1 className="text-xl font-bold text-gray-800">Loading Health Deal</h1>
  </div>
);

// Sentry setup
Sentry.init({
  dsn: 'https://07af7d10c3667f1935f7430e6c7ea169@o4508638870241280.ingest.de.sentry.io/4508639001706577',
  normalizeDepth: 5,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1,
  tracesSampleRate: 0.2,

  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration({
      stickySession: true,
      networkDetailAllowUrls: [
        'https://api.healthdeal.com.au/sales',
      ],
      networkRequestHeaders: ['X-Custom-Header'],
      networkResponseHeaders: ['X-Custom-Header'],
      maskAllInputs: !new URLSearchParams(window.location.search).has('test'),
      blockAllMedia: false,
    }),
  ],
  enabled: window.location.hostname === 'healthdeal.com.au',
  beforeSend: (event) => {
    const requestUrl = event.request?.url;
    if (requestUrl && requestUrl.includes('health-comparison')) {
      return event;
    }
    return null;
  },
});
const tagManagerArgs = {
  gtmId: 'GTM-5DJ9CKW',
};
TagManager.initialize(tagManagerArgs);
console.log('TagManager initialized');

// Add a small delay to ensure the loading indicator is visible before attempting to load the app
const rootElement = document.getElementById('root');
if (rootElement) {
  // First render just the loading indicator
  const tempRoot = createRoot(rootElement);
  tempRoot.render(<LoadingIndicator />);
  
  // After a small delay, render the full app with proper suspense
  setTimeout(() => {
    createRoot(rootElement).render(
      <MantineProvider theme={theme}>
        <Provider store={store}>
          <Suspense fallback={<LoadingIndicator />}>
            <App />
          </Suspense>
          <ToastContainer />
        </Provider>
        <Suspense fallback={null}>
          <TrustPilotCarousel />
        </Suspense>
      </MantineProvider>,
    );
  }, 100);
} else {
  console.error('Root element not found');
}
